import { useState, useRef, useEffect } from 'react';
import { Checkbox, FormLabel, Box, Radio, FormControlLabel, RadioGroup, FormGroup } from '@mui/material';
import { useRecoilValue, useRecoilState } from 'recoil';

import { sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import TextField from '@Components/Shared/Controls/TextField';
import TranslationText from '@Components/Utilities/TranslationText';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import translator from '@Components/Utilities/Translator';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import Modal from '@Components/Shared/Modals/Modal';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import Calendar from '@Components/MuiWrappers/Calendar';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { CharterFlightType } from '@Components/Utilities/SandhillsConstants';

const RequestACharterModal = (props) => {
    const today = new Date();
    const equipmentMessageID = props.Copy ? 0 : props.EquipmentMessageID;
    const [shouldRedirect, setShouldRedirect] = useState(props.TryRedirectSave);
    const [responseError, setResponseError] = useState(props.ErrorMessage || '');
    const routes = useRecoilValue(atoms.routes);
    const inputReferences = useRef({});
    const timeout = useRef(null);
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const currentUser = useRecoilValue(atoms.currentUser);
    const [showModal, setShowModal] = useRecoilState(atoms.showRaq);
    const [hideForm, setHideForm] = useState(false);
    const windowSize = useWindowSize();

    const aircraftTypes = ['Jet', 'Turboprop', 'Piston', 'Helicopter'];

    const flightTypes = [CharterFlightType.OneWay, CharterFlightType.RoundTrip];

    const [formData, setFormData] = useState({
        SelectedAircraftPrefType: props.SelectedAircraftPrefType ? props.SelectedAircraftPrefType : [],
        TypeOfFlight: props.TypeOfFlight ? props.TypeOfFlight : CharterFlightType.RoundTrip,
        DepartingDate: props.DepartingDate ? new Date(props.DepartingDate) : new Date(),
        ReturningDate: props.ReturningDate ? new Date(props.ReturningDate) : new Date(),
        NumberOfPassenger: props.NumberOfPassenger || '',
        DepartCityAirportCode: props.FromCityAirportCode || {},
        ToCityAirportCode: props.ToCityAirportCode || {},
        Comments: props.Comments || ''

    });
    useEffect(() => {
        if (shouldRedirect) {
            onSubmit();
            setShouldRedirect(false);
        }
    }, []);

    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('SelectedAircraftPrefType', translator.translate('sandhills.SelectedAircraftPrefType')),
            ruleRunner('TypeOfFlight', translator.translate('sandhills.typeofflight')),
            ruleRunner('DepartingDate', translator.translate('sandhills.datefrom'), ValidationRules.required),
            ruleRunner('ReturningDate', translator.translate('sandhills.dateto')),
            ruleRunner('NumberOfPassenger', translator.translate('sandhills.NumberOfPassenger'), ValidationRules.required, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1, false)),
            ruleRunner('DepartCityAirportCode', translator.translate('sandhills.tradesites.mvc.cityairportfrom'), ValidationRules.required),
            ruleRunner('ToCityAirportCode', translator.translate('sandhills.tradesites.mvc.cityairportto'), ValidationRules.required),
            ruleRunner('Comments', translator.translate('sandhills.comments'))
        ]);

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            setDisableSubmit(true);
            if (!currentUser.IsAuthenticated) {
                var qsStart = window.location.href.includes('?') ? '&' : '?';
                window.location.href = routes.SSO + '?to=' + encodeURIComponent(window.location.href + qsStart + 'DepartingDate=' + formData.DepartingDate.toISOString() + '&ReturningDate=' + formData.ReturningDate.toISOString() + '&Comments=' + formData.Comments + '&NumberOfPassenger=' + formData.NumberOfPassenger + '&TypeOfFlight=' + formData.TypeOfFlight + '&DepartCityAirportCode=' + formData.DepartCityAirportCode + '&ToCityAirportCode=' + formData.ToCityAirportCode);
            }
            else {
                onSubmit();
            }
        }
    };

    const getAutoCompleteUrl = (e, url) => {
        return url;
    };

    const getAutoCompleteData = (e) => {
        return {
            text: e.target.value
        };
    };

    const setSelectedAircraftPrefType = (event) => {
        let value = event.target.value;

        setFormData(previousState => {           
            let updatedSelectedAircraftPrefType = previousState.SelectedAircraftPrefType;
            if (updatedSelectedAircraftPrefType.includes(value)) {
                updatedSelectedAircraftPrefType = updatedSelectedAircraftPrefType.filter(item => item !== value);
            } else {
                updatedSelectedAircraftPrefType.push(value);
            }

            return {
                ...previousState, SelectedAircraftPrefType: updatedSelectedAircraftPrefType
            };
        });
    };

    const onChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const onAirportChange = (e) => {
        let id = e.target.id;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [id]: { AirportCodeDisplay: value } };
        });
    }

    const setFlightType = (e) => {
        setFormData(previousState => {
            return { ...previousState, TypeOfFlight: e.target.value };
        });
    }

    useEffect(() => {
        if (props.Industry && ((props.Edit && !props.ListingID) || props.Copy)) {
            getCategoriesForIndustry(props.Industry);
        }
    }, []);

    const inputFontSize = () => {
        if (windowSize.isMobile) {
            return '1rem';
        }
        else {
            return '14px';
        }
    }

    const sx = {
        '& .MuiOutlinedInput-root.Mui-focused': {
            '& > fieldset': {
                borderColor: 'black'
            }
        },
        backgroundColor: 'white',
        input: {
            fontSize: inputFontSize(),
            color: '#57595b'
        },
        button: {
            marginRight: '10px'
        },
        svg: {
            width: '15px',
            height: '15px'
        },
        width: '100%',
        height: '42px'

    };

    const onBeginDateChange = (date) => {
        setFormData(previousState => {
            return { ...previousState, DepartingDate: date };
        });
    };
    const onEndDateChange = (date) => {
        setFormData(previousState => {
            return { ...previousState, ReturningDate: date };
        });
    };

    const onSubmit = (token) => {
        sandhillsRequest({
            url: routes.CharterRequestSave,
            method: 'post',
            data: {
                EquipmentMessageID: props.Copy ? 0 : equipmentMessageID,
                EquipmentMessageStatus: props.Copy ? 1 : props.EquipmentMessageStatus,
                ListingID: props.ListingID,
                SelectedAircraftPrefType: formData.SelectedAircraftPrefType,
                TypeOfFlight: formData.TypeOfFlight,
                DepartingDate: formData.DepartingDate,
                ReturningDate: formData.ReturningDate,
                FromCityAirportCode: formData.DepartCityAirportCode,
                ToCityAirportCode: formData.ToCityAirportCode,
                NumberOfPassenger: formData.NumberOfPassenger,
                Comments: formData.Comments,
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            setSuccess(data.Success);
            setDisableSubmit(false);
            setValidationErrors(data.ValidationErrors || {});
            focusErroredField(data.ValidationErrors, inputReferences);
            if (data.Success) {
                setFormData(previousState => {
                    return {
                        ...previousState,
                        SelectedAircraftPrefType: data.SelectedAircraftPrefType || [],
                        TypeOfFlight: data.TypeOfFlight || '',
                        DepartingDate: data.DepartingDate || new Date(),
                        ReturningDate: data.ReturningDate || new Date(),
                        DepartCityAirportCode: data.FromCityAirportCode || '',
                        ToCityAirportCode: data.ToCityAirportCode || '',
                        NumberOfPassenger: data.NumberOfPassenger || '',
                        Comments: data.Comments || ''
                    };
                });
                setHideForm(true);
                timeout.current = closeModalTimer();
                if (props.setQuoteRequest) {
                    props.setQuoteRequest(previousState => {
                        return {
                            ...previousState,
                            SelectedAircraftPrefType: data.SelectedAircraftPrefType || [],
                            TypeOfFlight: data.TypeOfFlight || '',
                            DepartingDate: data.DepartingDate || new Date(),
                            ReturningDate: data.ReturningDate || new Date(),
                            DepartCityAirportCode: data.FromCityAirportCode || '',
                            ToCityAirportCode: data.ToCityAirportCode || '',
                            NumberOfPassenger: data.NumberOfPassenger || '',
                            Comments: data.Comments || '',
                            RequestAQuoteModel: {
                                ...previousState,
                                SelectedAircraftPrefType: data.SelectedAircraftPrefType || [],
                                TypeOfFlight: data.TypeOfFlight || '',
                                DepartingDate: data.DepartingDate || new Date(),
                                ReturningDate: data.ReturningDate || new Date(),
                                DepartCityAirportCode: data.FromCityAirportCode || '',
                                ToCityAirportCode: data.ToCityAirportCode || '',
                                NumberOfPassenger: data.NumberOfPassenger || '',
                                Comments: data.Comments || ''
                            }
                        };
                    });
                }
            } else {
                setResponseError(data.ErrorMessage || '');
                if (!currentUser.IsAuthenticated || (props.IsEmployee)) {
                    setHideForm(true);
                }
                setValidationErrors(data.ValidationErrors || {});
            }
        });
    };

    const resetModal = () => {
        setFormData({
            SelectedAircraftPrefType: props.SelectedAircraftPrefType ? props.SelectedAircraftPrefType : [],
            TypeOfFlight: props.TypeOfFlight ? props.TypeOfFlight : CharterFlightType.RoundTrip,
            DepartingDate: props.DepartingDate ? new Date(props.DepartingDate) : new Date(),
            ReturningDate: props.ReturningDate ? new Date(props.ReturningDate) : new Date(),
            NumberOfPassenger: props.NumberOfPassenger || '',
            DepartCityAirportCode: props.FromCityAirportCode || {},
            ToCityAirportCode: props.ToCityAirportCode || {},
            Comments: props.Comments || ''
        });
        setHideForm(false);
        setSuccess(false);
    };

    const closeModalTimer = () => setTimeout(() => {
        props.toggleModal ? props.toggleModal() : setShowModal(false);
        resetModal();
    }, 3000);

    const closeModal = () => {
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        props.toggleModal ? props.toggleModal() : setShowModal(false);
        resetModal();
    };

    return (
        <>
            {(showModal || props.showRequestAQuoteModal) &&
                <Modal containerClassName='feedback-modal' onEscapeKeyClose={closeModal}>
                    <Modal.Header onClose={closeModal}>
                        <Modal.Title>
                            {props.Edit ? <span>{translator.translate('sandhills.edit')} {translator.translate('sandhills.requestacharterquote')}</span> : props.Copy ? <span>{translator.translate('sandhills.tradepubwebsite.mvc.copy')} {translator.translate('sandhills.requestacharterquote')}</span> : ""}
                            {!props.Edit && !props.Copy && (props.ListingTitle ? <span>{translator.translate('sandhills.requestacharterquote')} for {props.ListingTitle}</span> : <span>{translator.translate('sandhills.requestacharterquote')}</span>)}
                        </Modal.Title>
                        {success &&
                            <>
                                {props.Edit && <p className='success'>{translator.translate('sandhills.quoterequestupdated')}!</p>}
                                {!props.Edit && <p className='success'>{translator.translate('sandhills.quoterequestsent')}!</p>}
                            </>}
                    </Modal.Header>
                    <Modal.Body>
                        <p id='description-label' className='hidden'><TranslationText tKey='sandhills.requestacharterquote' /></p>
                        <form className={'contact-form-modal' + (hideForm ? ' hide' : '')} name='feedbackForm' onSubmit={submitForm}>
                            <ValidationErrors errors={validationErrors} />
                            {responseError && <p className='error'>{responseError}</p>}
                            <div className='form-main-content'>
                                <div className='left-col'>
                                    <div className='form-el'>
                                        <FormLabel component="legend">{<TranslationText tKey='sandhills.typeofflight' />}:</FormLabel>
                                        <RadioGroup aria-label="position" name="flight-selector-radio-group" value={formData.TypeOfFlight} onChange={setFlightType} row>
                                            {flightTypes.map((flight) =>
                                                <FormControlLabel
                                                    value={flight}
                                                    control={<Radio />}
                                                    label={flight}
                                                    labelPlacement="end"
                                                />
                                            )}
                                        </RadioGroup>
                                    </div>
                                    <TextField
                                        id='NumberOfPassenger'
                                        name='NumberOfPassenger'
                                        value={formData.NumberOfPassenger}
                                        placeholder={translator.translate('sandhills.passengers')}
                                        type='text'
                                        label={<TranslationText tKey='sandhills.passengers' />}
                                        class='form-el'
                                        required={true}
                                        validationErrors={validationErrors.NumberOfPassenger}
                                        ref={(element) => inputReferences.current[`NumberOfPassenger`] = element}
                                        onFieldChanged={onChange}
                                    />
                                    <div className='form-el'>
                                        <TextField
                                            id='DepartCityAirportCode'
                                            name='DepartCityAirportCode'
                                            label={<TranslationText tKey='sandhills.tradesites.mvc.cityairportfrom' />}
                                            value={formData.DepartCityAirportCode?.AirportCodeDisplay}
                                            type='text'
                                            required={true}
                                            validationErrors={validationErrors.DepartCityAirportCode?.AirportCodeDisplay}
                                            ref={(element) => inputReferences.current[`DepartCityAirportCode`] = element}
                                            onFieldChanged={onAirportChange}
                                            updateField={(value, airportcodeid, cityid) => setFormData(previousState => {
                                                return { ...previousState, DepartCityAirportCode: { AirportCodeID: airportcodeid, AirportCityID: cityid, AirportCodeDisplay: value } };
                                            })}
                                            autoComplete={{
                                                ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                                postData: (e) => getAutoCompleteData(e)
                                            }}
                                        />
                                    </div>
                                    <div className='form-el'>
                                        <TextField
                                            id='ToCityAirportCode'
                                            labelId='ToCityAirportCode'
                                            name='ToCityAirportCode'
                                            label={<TranslationText tKey='sandhills.tradesites.mvc.cityairportto' />}
                                            value={formData.ToCityAirportCode?.AirportCodeDisplay}
                                            type='text'
                                            required={true}
                                            validationErrors={validationErrors.ToCityAirportCode?.AirportCodeDisplay}
                                            ref={(element) => inputReferences.current[`ToCityAirportCode`] = element}
                                            onFieldChanged={onAirportChange}
                                            updateField={(value, airportcodeid, cityid) => setFormData(previousState => {
                                                return { ...previousState, ToCityAirportCode: { AirportCodeID: airportcodeid, AirportCityID: cityid, AirportCodeDisplay: value } };
                                            })}
                                            autoComplete={{
                                                ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                                postData: (e) => getAutoCompleteData(e)
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className='right-col'>
                                    <div className='form-el'>
                                        <FormLabel component="legend">{<TranslationText tKey='sandhills.typeofaircraft' />}:</FormLabel>
                                        <FormGroup aria-label="position" name="aircraft-selector-radio-group" value={formData.SelectedAircraftPrefType} row>
                                            {aircraftTypes.map((aircraft) =>
                                                <FormControlLabel
                                                    key={aircraft}
                                                    value={aircraft}
                                                    control={<Checkbox checked={formData.SelectedAircraftPrefType.includes(aircraft)} onChange={setSelectedAircraftPrefType}/>}
                                                    label={aircraft}
                                                    labelPlacement="end"
                                                />
                                            )}
                                        </FormGroup>
                                    </div>
                                    <div className='form-el'>
                                        <label htmlFor='DepartingDate' className='bold'><TranslationText tKey='Sandhills.DateFrom' /> <span className='required red' /></label>
                                        <Box sx={{ width: '100%' }}>
                                            <Calendar
                                                id='DepartingDate'
                                                value={formData.DepartingDate}
                                                onChange={onBeginDateChange}
                                                inputFormat='MM/dd/yyyy'
                                                PopperProps={{ disablePortal: true }}
                                                minDate={today}
                                                validationErrors={validationErrors.DepartingDate}
                                                DialogProps={{
                                                    sx: {
                                                        zIndex: 1600
                                                    }
                                                }}
                                                InputAdornmentProps={{
                                                    sx: {
                                                        margin: '0px'
                                                    }
                                                }}
                                                sx={sx}
                                            />
                                        </Box>
                                    </div>
                                    {formData.TypeOfFlight == CharterFlightType.RoundTrip &&
                                        <div className='form-el'>
                                            <label htmlFor='ReturningDate' className='bold'><TranslationText tKey='Sandhills.DateTo' /> <span className='required red' /></label>
                                            <Box sx={{ width: '100%' }}>
                                                <Calendar
                                                    id='ReturningDate'
                                                    value={formData.ReturningDate}
                                                    onChange={onEndDateChange}
                                                    minDate={formData.ReturningDate}
                                                    inputFormat='MM/dd/yyyy'
                                                    PopperProps={{ disablePortal: true }}
                                                    validationErrors={validationErrors.ReturningDate}
                                                    DialogProps={{
                                                        sx: {
                                                            zIndex: 1600
                                                        }
                                                    }}
                                                    InputAdornmentProps={{
                                                        sx: {
                                                            margin: '0px'
                                                        }
                                                    }}
                                                    sx={sx}
                                                />
                                            </Box>
                                        </div>}
                                    <TextField
                                        id='Comments'
                                        name='Comments'
                                        value={formData.Comments}
                                        placeholder={translator.translate('sandhills.tradepubwebSite.mvc.comments')}
                                        type='textarea'
                                        label={<TranslationText tKey='sandhills.tradepubwebSite.mvc.comments' />}
                                        class='form-el text-area-container'
                                        inputClass='text-area'
                                        validationErrors={validationErrors.Comments}
                                        ref={(element) => inputReferences.current[`Comments`] = element}
                                        onFieldChanged={onChange}
                                    />
                                </div>
                            </div>
                            <div className='form-bottom'>
                                <input className='ts-button recaptcha-submit-button button' type='submit' id='request-a-quote-submit-button' name='request-a-quote-submit-button' value={translator.translate('sandhills.tradepubwebsite.mvc.submit')} disabled={disableSubmit} />
                            </div>
                            {disableSubmit && <LoadingRing backdrop />}
                        </form>
                    </Modal.Body>
                </Modal>
            }
        </>
    );
};

export default RequestACharterModal;